import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { localization } from '@ocean/configs'
import { CommonUtils, DictionariesUtils, SortUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { createApiAction, ApiAction, MiddlewareApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { namespace } from 'modules/virtualClassrooms/common/localization'
import {
  getListVirtualEventTasks,
  deleteVirtualEventTasks,
  addVirtualEventTask,
  updateVirtualEventTask,
  addVirtualEventTaskWithFile,
  getListVirtualEventPresentationFiles,
  deleteVirtualEventPresentationFile,
  updateVirtualEventPresentationFile,
  uploadVirtualEventPresentationFile,
  getListSurveys,
  getListElearnings,
  getListRapidElearnings,
  getCrewEvaluationReferences,
  getCrewEvaluationDetailedTestCriteria,
  getCrewEvaluationStcwTestCriteria,
  getCrewEvaluationTestDefinitions,
  getVirtualEventTask,
} from 'modules/virtualClassrooms/manager/EventDetails/api/tasksAndResourcesService'
import {
  AddVirtualEventTaskModel,
  AddVirtualEventTaskWithFileModel,
  UpdateVirtualEventTaskModel,
  Availability,
  VirtualEventTask,
  CrewEvaluationReferences,
  TestType,
  TestDefinition,
} from 'modules/virtualClassrooms/manager/EventDetails/types/tasksAndResources'
import { TasksAndResourcesUtils } from 'modules/virtualClassrooms/manager/EventDetails/utils'
import {
  TasksAndResourcesState,
  ApiListVirtualEventTasksResponse,
  ReloadVirtualEventTasksParams,
  ApiListVirtualEventPresentationFilesResponse,
  ApiListElearningsResponse,
  ApiListRapidElearningsResponse,
  ApiListSurveysResponse,
  ApiCrewEvaluationReferencesResponse,
  ApiGetVirtualEventTaskResponse,
} from './types'

const { i18n } = localization

const I18_PATH = `${namespace}:manager.eventDetails.tasksAndResources`

const initialState: TasksAndResourcesState = {
  eventId: null,
  virtualEventTaskListData: {},
  deleteVirtualEventTasksRequest: {},
  addVirtualEventTaskRequest: {},
  addVirtualEventTaskWithFileRequest: {},
  updateVirtualEventTaskRequest: {},
  uploadVirtualEventTaskFileRequest: {},
  listVirtualEventPresentationFiles: {},
  deleteVirtualEventPresentationFileRequest: {},
  updateVirtualEventPresentationFileRequest: {},
  uploadVirtualEventPresentationFileRequest: {},
  elearningListData: {},
  rapidElearningListData: {},
  surveyListData: {},
  crewEvaluationReferencesData: {},
  getVirtualEventTaskRequest: {},
}

const eventDetailsTasksAndResourcesSlice = createSlice({
  name: 'virtualClassroomManagerEventDetailsTasksAndResources',
  initialState,
  reducers: {
    eventIdSetted(state, action: PayloadAction<string>) {
      state.eventId = action.payload
    },
    clearVirtualEventTasksData(state) {
      state.eventId = initialState.eventId
      state.virtualEventTaskListData = initialState.virtualEventTaskListData
    },
    virtualEventTaskListLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListVirtualEventTasksResponse>>
    ) {
      state.virtualEventTaskListData = action.payload
    },
    deleteVirtualEventTasksRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.deleteVirtualEventTasksRequest = action.payload
    },
    addVirtualEventTaskRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.addVirtualEventTaskRequest = action.payload
    },
    addVirtualEventTaskWithFileRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.addVirtualEventTaskWithFileRequest = action.payload
    },
    updateVirtualEventTaskRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.updateVirtualEventTaskRequest = action.payload
    },
    surveyListLoaded(state, action: PayloadAction<ApiMiddlewarePayload<ApiListSurveysResponse>>) {
      state.surveyListData = action.payload
    },
    elearningListLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListElearningsResponse>>
    ) {
      state.elearningListData = action.payload
    },
    rapidElearningListLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListRapidElearningsResponse>>
    ) {
      state.rapidElearningListData = action.payload
    },
    crewEvaluationReferencesDataLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCrewEvaluationReferencesResponse>>
    ) {
      state.crewEvaluationReferencesData = action.payload
    },
    listVirtualEventPresentationFilesLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListVirtualEventPresentationFilesResponse>>
    ) {
      state.listVirtualEventPresentationFiles = action.payload
    },
    clearListVirtualEventPresentationFiles(state) {
      state.listVirtualEventPresentationFiles = initialState.listVirtualEventPresentationFiles
    },
    deleteVirtualEventPresentationFileRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload>
    ) {
      state.deleteVirtualEventPresentationFileRequest = action.payload
    },
    updateVirtualEventPresentationFileRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload>
    ) {
      state.updateVirtualEventPresentationFileRequest = action.payload
    },
    uploadVirtualEventPresentationFileRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload>
    ) {
      state.uploadVirtualEventPresentationFileRequest = action.payload
    },
    getVirtualEventTaskRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiGetVirtualEventTaskResponse>>
    ) {
      state.getVirtualEventTaskRequest = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  eventIdSetted,
  virtualEventTaskListLoaded,
  deleteVirtualEventTasksRequested,
  addVirtualEventTaskRequested,
  addVirtualEventTaskWithFileRequested,
  updateVirtualEventTaskRequested,
  listVirtualEventPresentationFilesLoaded,
  deleteVirtualEventPresentationFileRequested,
  updateVirtualEventPresentationFileRequested,
  uploadVirtualEventPresentationFileRequested,
  getVirtualEventTaskRequested,
} = eventDetailsTasksAndResourcesSlice.actions
export const {
  clearListVirtualEventPresentationFiles,
  surveyListLoaded,
  elearningListLoaded,
  rapidElearningListLoaded,
  crewEvaluationReferencesDataLoaded,
} = eventDetailsTasksAndResourcesSlice.actions
export const { clearVirtualEventTasksData } = eventDetailsTasksAndResourcesSlice.actions
export default eventDetailsTasksAndResourcesSlice.reducer

export const getEventRelatedTasksPartition = createSelector(
  (tasks: VirtualEventTask[] = []) => tasks,
  tasks => CommonUtils.partition<VirtualEventTask>(tasks, ({ isPostEvent }) => isPostEvent)
)

const handleSetEventId =
  (response: ApiMiddlewarePayload, eventId: Guid): AppThunk =>
  async dispatch => {
    dispatch(virtualEventTaskListLoaded(response))
    if (!response.error) dispatch(eventIdSetted(eventId))
  }

export const loadListVirtualEventTasksData = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListVirtualEventTasks(eventId),
    },
    payloadAction: virtualEventTaskListLoaded,
    nextAction: {
      action: handleSetEventId,
      params: eventId,
    },
  })
}

export const reloadListVirtualEventTasksData = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListVirtualEventTasks(eventId),
    },
    payloadAction: virtualEventTaskListLoaded,
    getResultToKeep: ({ virtualClassroomManagerEventDetailsTasksAndResources }) =>
      virtualClassroomManagerEventDetailsTasksAndResources.virtualEventTaskListData.result,
    shouldShowGlobalLoading: true,
  })
}

const handleReloadVirtualEventTasks =
  (response: ApiMiddlewarePayload, params: ReloadVirtualEventTasksParams): AppThunk =>
  async (dispatch, getState) => {
    const { eventId } = getState().virtualClassroomManagerEventDetailsTasksAndResources
    const { payloadAction } = params
    dispatch(payloadAction(response))
    if (!response.error)
      dispatch(reloadListVirtualEventTasksData(eventId || '') as MiddlewareApiAction)
  }

export const deleteVirtualEventTasksRequest = (taskIds: string[]): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => deleteVirtualEventTasks(taskIds),
    },
    nextAction: {
      action: handleReloadVirtualEventTasks,
      params: { payloadAction: deleteVirtualEventTasksRequested },
    },
    payloadAction: deleteVirtualEventTasksRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

export const addVirtualEventTaskRequest = (formData: AddVirtualEventTaskModel): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => addVirtualEventTask(formData),
    },
    nextAction: {
      action: handleReloadVirtualEventTasks,
      params: { payloadAction: addVirtualEventTaskRequested },
    },
    payloadAction: addVirtualEventTaskRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

export const addVirtualEventTaskWithFileRequest = (
  formData: AddVirtualEventTaskWithFileModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => addVirtualEventTaskWithFile(formData),
    },
    nextAction: {
      action: handleReloadVirtualEventTasks,
      params: { payloadAction: addVirtualEventTaskWithFileRequested },
    },
    payloadAction: addVirtualEventTaskWithFileRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

export const updateVirtualEventTaskRequest = (formData: UpdateVirtualEventTaskModel): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => updateVirtualEventTask(formData),
    },
    nextAction: {
      action: handleReloadVirtualEventTasks,
      params: { payloadAction: updateVirtualEventTaskRequested },
    },
    payloadAction: updateVirtualEventTaskRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

const handleUpdateVirtualEventAssessmentTask =
  (
    response: ApiMiddlewarePayload<ApiGetVirtualEventTaskResponse>,
    formData: UpdateVirtualEventTaskModel
  ): AppThunk =>
  async (dispatch, getState) => {
    const { error, result } = response
    const { assessment } = result?.request.task || {}
    dispatch(getVirtualEventTaskRequested(response))
    if (!error && assessment) {
      const assessmentTestCriteria = TasksAndResourcesUtils.getTestCriteriaIds(assessment)
      const formDataWithAssessment: UpdateVirtualEventTaskModel = {
        ...formData,
        assessmentProperties: {
          cesTestType: assessment.testType,
          ...assessmentTestCriteria,
        },
      }
      dispatch(updateVirtualEventTaskRequest(formDataWithAssessment) as MiddlewareApiAction)
    }
  }

export const updateVirtualEventAssessmentTaskRequest = (
  formData: UpdateVirtualEventTaskModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => getVirtualEventTask(formData.taskId),
    },
    nextAction: {
      action: handleUpdateVirtualEventAssessmentTask,
      params: formData,
    },
    payloadAction: getVirtualEventTaskRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

export const getActiveSortedActivities = createSelector(
  (objectArray?: Record<string, any>[]) => objectArray,
  objectArray => DictionariesUtils.getFilteredAndSortedItems({ objectArray })
)

export const getSortedTestDefinitions = createSelector(
  (testDefinitions?: TestDefinition[]) => testDefinitions,
  testDefinitions =>
    testDefinitions && [...testDefinitions].sort(SortUtils.sortByMultipleKeys(['name']))
)

export const loadListSurveysData = (): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListSurveys(),
    },
    payloadAction: surveyListLoaded,
    shouldCallApi: ({ virtualClassroomManagerEventDetailsTasksAndResources }) =>
      !virtualClassroomManagerEventDetailsTasksAndResources.surveyListData.result,
  })
}

export const loadListElearningsData = (): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListElearnings(),
    },
    payloadAction: elearningListLoaded,
    shouldCallApi: ({ virtualClassroomManagerEventDetailsTasksAndResources }) =>
      !virtualClassroomManagerEventDetailsTasksAndResources.elearningListData.result,
  })
}

export const loadListRapidElearningsData = (): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListRapidElearnings(),
    },
    payloadAction: rapidElearningListLoaded,
    shouldCallApi: ({ virtualClassroomManagerEventDetailsTasksAndResources }) =>
      !virtualClassroomManagerEventDetailsTasksAndResources.rapidElearningListData.result,
  })
}

export const getActiveSortedCrewEvaluationReferences = createSelector(
  (references?: CrewEvaluationReferences) => references,
  references => {
    return (
      references &&
      (Object.keys(references).reduce((result, referenceName) => {
        let reference
        const referenceKey = referenceName as keyof CrewEvaluationReferences
        switch (referenceKey) {
          case 'testTypes':
            reference = DictionariesUtils.getFilteredAndSortedItems({
              objectArray: references[referenceKey],
              filterFunction: item => (item as TestType).canBeRun,
            })
            break
          default:
            reference = DictionariesUtils.getFilteredAndSortedItems({
              objectArray: references[referenceKey],
            })
        }
        result[referenceKey] = reference
        return result
      }, {} as Record<string, any>) as CrewEvaluationReferences)
    )
  }
)

export const loadCrewEvaluationReferencesData = (): ApiAction => {
  return createApiAction({
    apiCalls: {
      baseReferences: getCrewEvaluationReferences,
      testDefinitions: getCrewEvaluationTestDefinitions,
      stcwTestCriteria: getCrewEvaluationStcwTestCriteria,
      detailedTestCriteria: getCrewEvaluationDetailedTestCriteria,
    },
    payloadAction: crewEvaluationReferencesDataLoaded,
    shouldCallApi: ({ virtualClassroomManagerEventDetailsTasksAndResources }) =>
      !virtualClassroomManagerEventDetailsTasksAndResources.crewEvaluationReferencesData.result,
  })
}

const handleLoadListVirtualEventPresentationFiles =
  (response: ApiMiddlewarePayload, eventId: Guid): AppThunk =>
  async dispatch => {
    dispatch(listVirtualEventPresentationFilesLoaded(response))
    if (!response.error) dispatch(eventIdSetted(eventId))
  }

export const loadListVirtualEventPresentationFiles = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      files: () => getListVirtualEventPresentationFiles(eventId),
    },
    payloadAction: listVirtualEventPresentationFilesLoaded,
    nextAction: {
      action: handleLoadListVirtualEventPresentationFiles,
      params: eventId,
    },
  })
}

export const reloadListVirtualEventPresentationFiles = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      files: () => getListVirtualEventPresentationFiles(eventId),
    },
    payloadAction: listVirtualEventPresentationFilesLoaded,
    getResultToKeep: ({ virtualClassroomManagerEventDetailsTasksAndResources }) =>
      virtualClassroomManagerEventDetailsTasksAndResources.listVirtualEventPresentationFiles.result,
    shouldShowGlobalLoading: true,
  })
}

const handleReloadListVirtualEventPresentationFiles =
  (response: ApiMiddlewarePayload, payloadAction: ApiAction['payloadAction']): AppThunk =>
  async (dispatch, getState) => {
    const { eventId } = getState().virtualClassroomManagerEventDetailsTasksAndResources
    dispatch(payloadAction(response))
    if (!response.error)
      dispatch(reloadListVirtualEventPresentationFiles(eventId || '') as MiddlewareApiAction)
  }

export const uploadVirtualEventPresentationFileRequest = (eventId: Guid, file: File): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => uploadVirtualEventPresentationFile(eventId, file),
    },
    payloadAction: uploadVirtualEventPresentationFileRequested,
    nextAction: {
      action: handleReloadListVirtualEventPresentationFiles,
      params: uploadVirtualEventPresentationFileRequested,
    },
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

export const deleteVirtualEventPresentationFileRequest = (fileId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => deleteVirtualEventPresentationFile(fileId),
    },
    payloadAction: deleteVirtualEventPresentationFileRequested,
    nextAction: {
      action: handleReloadListVirtualEventPresentationFiles,
      params: deleteVirtualEventPresentationFileRequested,
    },
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    successMessage: i18n.t(`${I18_PATH}.presentationFiles.successDelete`),
  })
}

export const updateVirtualEventPresentationFileRequest = (
  fileId: Guid,
  availability: Availability
): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => updateVirtualEventPresentationFile(fileId, availability),
    },
    payloadAction: updateVirtualEventPresentationFileRequested,
    nextAction: {
      action: handleReloadListVirtualEventPresentationFiles,
      params: updateVirtualEventPresentationFileRequested,
    },
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}
